import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isFunction from 'lodash/isFunction';
import ReactTooltip from 'react-tooltip';
import { v4 as getUUID } from 'uuid';
import sanitizeHtml from 'sanitize-html';
import linkifyText from '@adsk/bim360-matrix-react-helpers/es/linkifyText';
import defaultSanitizeOptions from '@adsk/bim360-matrix-react-helpers/es/defaultSanitizeOptions';
import isRTL from '@adsk/bim360-matrix-react-helpers/es/isRTL';
import isContentOverflowingElement from '@adsk/bim360-matrix-react-helpers/es/isContentOverflowingElement';
import parseNewLines from '@adsk/bim360-matrix-react-helpers/es/parseNewLines';
import deprecate from '@adsk/bim360-matrix-react-helpers/es/deprecate';

import './_Text.scss';

/**
 * A Text component that knows to support RTL texts, and truncating text
 */
export default class Text extends Component {
  constructor(props) {
    super(props);

    if (props.ellipsis && !props.hideTooltip) {
      deprecate(
        "Tooltips within Text will no longer be supported, please try working with other tooltiping solutions because there's no alternative for it in this package at the moment."
      );
    }
    this.state = { truncated: false };
  }

  /**
   * Checks whether or not the cell data is overflowing
   */
  calculateContentOverflow() {
    if (this.container) {
      this.setState({ truncated: isContentOverflowingElement(this.container) });
    }
  }

  /**
   * componentDidMount
   */
  componentDidMount() {
    this.calculateContentOverflow();
  }

  render() {
    const {
      children,
      parseNewLines: shouldParseNewLines,
      onClick,
      ellipsis,
      className,
      hasHTML,
      sanitizeOptions,
      linkify,
      containerClassName,
      containerStyle,
      tooltipClassName,
      hideTooltip,
      ...rest
    } = this.props;

    let text = children;
    text = (shouldParseNewLines && parseNewLines(text)) || text;

    const handleOnClick = () => onClick();
    const isClickable = isFunction(onClick);
    const onClickHandler = isClickable ? handleOnClick : null;
    const contentFieldClasses = classNames(
      'Text__content',
      {
        'Text__content--rtl': isRTL(text),
        'Text__content--clickable': isClickable,
        'Text__content--ellipsis': ellipsis
      },
      className
    );

    const tooltipId = getUUID();
    const propForAttachingTooltips = ellipsis ? { 'data-tip': true, 'data-for': tooltipId } : {};
    const renderHtmlProp = {};
    if (hasHTML) {
      renderHtmlProp.dangerouslySetInnerHTML = { __html: sanitizeHtml(text, sanitizeOptions) };
    } else if (linkify) {
      renderHtmlProp.dangerouslySetInnerHTML = { __html: linkifyText(text) };
    }

    const containerProps = {
      className: classNames('Text__container', containerClassName),
      style: containerStyle,
      ...rest
    };

    const tooltipClass = classNames('Text__tooltip', tooltipClassName);

    return (
      <div {...containerProps}>
        <div
          {...propForAttachingTooltips}
          {...renderHtmlProp}
          ref={node => {
            this.container = node;
          }}
          className={contentFieldClasses}
          onClick={onClickHandler}
        >
          {renderHtmlProp.dangerouslySetInnerHTML ? null : text}
        </div>
        {!hideTooltip && this.props.ellipsis ? (
          <ReactTooltip
            class={tooltipClass}
            place="bottom"
            effect="solid"
            id={tooltipId}
            disable={hideTooltip || !this.state.truncated}
          >
            {text}
          </ReactTooltip>
        ) : null}
      </div>
    );
  }
}

Text.DefaultSanitizeOptions = defaultSanitizeOptions();

Text.defaultProps = {
  sanitizeOptions: Text.DefaultSanitizeOptions
};

Text.propTypes = {
  /**
   * The text to display
   */
  children: PropTypes.node,
  /**
   * ClassName to for the element that contains the text
   */
  className: PropTypes.string,
  /**
   * ClassName for the element that contains the text element. We have an element wrapping the text element, since this
   * container contains both the text element and the tooltip. Sometimes it's necessary to pass it also style, such as width.
   */
  containerClassName: PropTypes.string,
  /**
   * Inner styling for the container
   */
  containerStyle: PropTypes.object,
  /**
   * On click handler to set clickable behavior on the text
   */
  onClick: PropTypes.func,
  /**
   * Pass true to replace newline characters with <br />
   */
  parseNewLines: PropTypes.bool,
  /**
   * Whether to truncate text with an elipssis. If set to true, don't forget to set the component with a width.
   */
  ellipsis: PropTypes.bool,
  /**
   * Hide tooltip with full text when text is truncated
   */
  hideTooltip: PropTypes.bool,
  /**
   * Whether there is HTML inside and should be rendered as such. SanitizeHtml will be used on Children. The sanitization options can be configured through `sanitizeOptions` props.
   */
  hasHTML: PropTypes.bool,
  /**
   * This prop is used only when `hasHTML` is true. For more info see https://github.com/punkave/sanitize-html
   */
  sanitizeOptions: PropTypes.object,
  /**
   * Whether to convert url and email strings to anchors. default false
   */
  linkify: PropTypes.bool,
  /**
   * ClassName for the tooltip element
   */
  tooltipClassName: PropTypes.string
};
